/**
 * field-config.js
 *
 * Centralized field configuration — the single source of truth for every
 * form field's selector, input formatter, and validation rule.
 *
 * To add a new field: add an entry here. Everything else adapts automatically.
 */

const translations = window.crTranslations || {};

/** @type {Record<string, { selector: string, type: 'input'|'checkbox', format?: Function, validate?: Function }>} */
export const FIELD_CONFIG = {
    cardNumber: {
        selector: '#cr-card-number',
        type: 'input',
        format: (val) => val.replace(/\D/g, '').substring(0, 8),
        validate: (val) => {
            if (!val) return translations.error_card_required;
            if (val.length !== 8) return translations.error_card_short;
            return '';
        },
    },

    name: {
        selector: '#cr-name',
        type: 'input',
        format: (val) => val.substring(0, 100), // Prevent excessively long names
        validate: (val) =>
            !val.trim() ? translations.error_name_required : '',
    },

    birthDate: {
        selector: '#cr-birth-date',
        type: 'input',
        format: (val) => {
            const digits = val.replace(/\D/g, '').substring(0, 8);
            if (digits.length <= 4) return digits;
            if (digits.length <= 6)
                return `${digits.substring(0, 4)} ${digits.substring(4, 6)}`;
            return `${digits.substring(0, 4)} ${digits.substring(4, 6)} ${digits.substring(6, 8)}`;
        },
        validate: (val) => {
            const trimmed = val.trim();
            if (!trimmed) return ''; // Optional field
            const digits = trimmed.replace(/\D/g, '');
            if (digits.length !== 8) {
                return translations.error_birthdate_invalid;
            }

            const year = parseInt(digits.substring(0, 4));
            const month = parseInt(digits.substring(4, 6));
            const day = parseInt(digits.substring(6, 8));

            const currentYear = new Date().getFullYear();
            if (year < 1900 || year > currentYear) {
                return translations.error_birthdate_invalid;
            }

            if (month < 1 || month > 12) {
                return translations.error_birthdate_invalid;
            }

            // Use Date object to check if the day is valid for that month/year
            const date = new Date(year, month - 1, day);
            const isValid =
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day;

            if (!isValid) {
                return translations.error_birthdate_invalid;
            }

            return '';
        },
    },

    city: {
        selector: '#cr-city',
        type: 'input',
        format: (val) => val.substring(0, 100),
        validate: (val) =>
            !val.trim() ? translations.error_city_required : '',
    },

    phone: {
        selector: '#cr-phone',
        type: 'input',
        format: (val) => {
            let digits = val.replace(/\D/g, '');
            // Limit to 8 digits (after the static +370 prefix)
            return digits.substring(0, 8);
        },
        validate: (val) => {
            const digits = val.replace(/\D/g, '');
            if (digits.length === 0) return translations.error_phone_required;
            if (digits.length < 8) return translations.error_phone_invalid;
            return '';
        },
    },

    email: {
        selector: '#cr-email',
        type: 'input',
        format: (val) => val.trim().substring(0, 150),
        validate: (val) => {
            const trimmed = val.trim();
            if (trimmed) {
                const regex =
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!regex.test(trimmed))
                    return translations.error_email_invalid;
            }
            return '';
        },
    },


    agreeEmail: { selector: '#cr-agree-email', type: 'checkbox' },
    agreeSms: { selector: '#cr-agree-sms', type: 'checkbox' },

    agreeTerms: {
        selector: '#cr-agree-terms',
        type: 'checkbox',
        validate: (checked) =>
            !checked ? translations.error_terms_required : '',
    },
};
